import React, { Fragment, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { useOnScreen } from 'site-modules/shared/hooks/use-on-screen';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { ShareThisChart } from 'site-modules/shared/components/share-this-chart/share-this-chart';

import './self-driving-cars.scss';

const laneWhite = getStaticImageUrl('/img/self-driving-cars/lane-white.svg');
const laneYellow = getStaticImageUrl('/img/self-driving-cars/lane-yellow.svg');
const path = getStaticImageUrl('/img/self-driving-cars/path.svg');
const mainCar0 = getStaticImageUrl('/img/self-driving-cars/main-car-0.svg');
const mainCar1 = getStaticImageUrl('/img/self-driving-cars/main-car-1.svg');
const mainCar2 = getStaticImageUrl('/img/self-driving-cars/main-car-2.svg');
const mainCar3 = getStaticImageUrl('/img/self-driving-cars/main-car-3.svg');
const mainCar4 = getStaticImageUrl('/img/self-driving-cars/main-car-4.svg');
const mainCar5 = getStaticImageUrl('/img/self-driving-cars/main-car-5.svg');
const car = getStaticImageUrl('/img/self-driving-cars/car.svg');
const halo = getStaticImageUrl('/img/self-driving-cars/halo.svg');
const steeringWheel = getStaticImageUrl('/img/self-driving-cars/steering-wheel.svg');
const logoBlue = getStaticImageUrl('/img/self-driving-cars/logo-blue.svg');

const SELF_DRIVING_CARS_ARTICLE = 'https://www.edmunds.com/car-buying/self-driving-cars.html';
const LEVEL_MAP = [
  {
    name: 'Minimal Assistance',
    description: (
      <Fragment>
        <span className="fw-bold">You&apos;re driving</span>, but the car can provide alerts and help in emergencies.
      </Fragment>
    ),
    driverPercent: 99,
    mainCar: mainCar0,
  },
  {
    name: 'Basic Assistance',
    description: (
      <Fragment>
        <span className="fw-bold">You&apos;re driving</span>, but the car can either manage your speed or help keep you
        in your lane.
      </Fragment>
    ),
    driverPercent: 90,
    mainCar: mainCar1,
  },
  {
    name: 'Additional Assistance',
    description: (
      <Fragment>
        <span className="fw-bold">You&apos;re driving</span>, but the car can simultaneously manage your speed and keep
        you in your lane.
      </Fragment>
    ),
    driverPercent: 75,
    mainCar: mainCar2,
  },
  {
    name: 'Conditional Automated Driving',
    description: (
      <Fragment>
        <span className="fw-bold">The car can drive itself in certain situations</span>, but you must be alert and ready
        to take over.
      </Fragment>
    ),
    driverPercent: 55,
    mainCar: mainCar3,
  },
  {
    name: 'Advanced Automated Driving',
    description: (
      <Fragment>
        <span className="fw-bold">The car can drive itself without your oversight</span>, but only in certain
        situations.
      </Fragment>
    ),
    driverPercent: 25,
    mainCar: mainCar4,
  },
  {
    name: 'Full Automated Driving',
    description: (
      <Fragment>
        <span className="fw-bold">The car can drive itself without your oversight in any situation.</span>
      </Fragment>
    ),
    driverPercent: 0,
    mainCar: mainCar5,
  },
];

const LEVEL_CONTAINER_STYLES = { opacity: '0' };

export function SelfDrivingCarsUI({ className, isMobile }) {
  const [level, setLevel] = useState(0);
  const [isPaused, togglePause] = useToggle(false);
  const [isTransition, setTransition] = useState(true);
  const containerRef = useRef();
  const [isOnScreen] = useOnScreen(containerRef);

  const nextLevel = (level + 1) % LEVEL_MAP.length;
  const prevLevel = level ? level - 1 : LEVEL_MAP.length - 1;
  const currentDriverPercent = LEVEL_MAP[level].driverPercent;

  function onAnimationEnd({ currentTarget: { dataset } }) {
    setLevel((parseInt(dataset.level, 10) + 1) % LEVEL_MAP.length);
    setTransition(true);
  }

  function onAnimationStart() {
    setTransition(false);
  }

  function setPrev() {
    setLevel(prevLevel);
    setTransition(true);
  }
  function setNext() {
    setLevel(nextLevel);
    setTransition(true);
  }

  const playerButtons = [
    { className: 'prev', label: 'Previous slide', iconClassName: 'icon-backward21', onClick: setPrev },
    {
      className: 'play-pause',
      label: isPaused ? 'Play' : 'Pause',
      iconClassName: isPaused ? 'icon-play41' : 'icon-pause21',
      onClick: togglePause,
    },
    { className: 'next', label: 'Next slide', iconClassName: 'icon-forward31', onClick: setNext },
  ];

  return (
    <div
      ref={containerRef}
      className={classnames('self-driving-cars bg-cool-gray-80 w-100 pos-r pb-3 pb-md-0', className)}
    >
      {LEVEL_MAP.map(({ name, description, driverPercent, mainCar }, index) => {
        const percentsMap = [
          { label: 'Driver', percent: driverPercent, wheelPercent: currentDriverPercent },
          { label: 'Car', percent: 100 - driverPercent, wheelPercent: 100 - currentDriverPercent },
        ];

        return (
          <div
            key={driverPercent}
            className={classnames('level-container d-flex flex-column', `level-${index}`, {
              ready: isTransition && (index === nextLevel || index === prevLevel),
              active: level === index,
              paused: isPaused || !isOnScreen,
            })}
            style={
              index
                ? {
                    ...LEVEL_CONTAINER_STYLES,
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '100%',
                  }
                : LEVEL_CONTAINER_STYLES
            }
            aria-hidden={level !== index}
          >
            <div
              className="description-part px-0_5 ps-md-1_5 pt-1_5"
              style={isMobile ? {} : { position: 'absolute', top: '0', left: '0', width: '50%' }}
            >
              <div className="levels-text text-blue-50 size-12 fw-bold mb-0_25">LEVELS OF SELF-DRIVING</div>
              <div
                className={classnames('text-cool-gray-30 fw-medium size-24 mb-0_25', {
                  'text-nowrap': !isMobile,
                  'name-line2': isMobile,
                })}
              >
                Level {index} - {name}
              </div>
              <div className="text-gray-darker mb-1_5">
                <span className="size-10 d-inline-block me-0_5">Powered by</span>
                <img height={13} src={logoBlue} alt="Edmunds" />
              </div>
              <div className="mb-1_5 d-flex">
                {percentsMap.map(({ label, percent, wheelPercent }, percentIndex) => (
                  <div className={classnames('d-flex', { 'me-2_5': !percentIndex })} key={label}>
                    <img
                      height={52}
                      width={52}
                      src={steeringWheel}
                      alt=""
                      className="steering-wheel me-0_5"
                      style={{ backgroundSize: `100% ${wheelPercent}%` }}
                    />
                    <div>
                      <div className="text-blue-40 fw-bold small">{label}</div>
                      <div
                        className={classnames('percent-text size-30', {
                          'text-cool-gray-30 fw-medium': percent >= 50,
                          'text-cool-gray-50': percent < 50,
                        })}
                      >
                        {percent}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="pe-xl-3_5">{description}</div>
            </div>
            <div className="w-100 ms-auto" style={{ maxWidth: '630px' }}>
              <AspectRatioContainer aspectRatio="1:1" className="w-100">
                <div className="animation-part h-100 overflow-hidden">
                  <div className="pos-a top-0 left-0 w-100 h-100" aria-hidden>
                    <img className="path pos-a" src={path} alt="" />
                    <img className="lane-left pos-a center-y" src={laneWhite} alt="" />
                    <img className="lane-left yellow pos-a center-y" src={laneYellow} alt="" />
                    <img className="lane-right pos-a center-y" src={laneWhite} alt="" />
                    <img className="lane-right yellow pos-a center-y" src={laneYellow} alt="" />
                    <img className="car-left pos-a" src={car} alt="" />
                    <img className="car-center pos-a center-x" src={car} alt="" />
                    <img className="car-right pos-a" src={car} alt="" />
                    <img className="halo-left pos-a" src={halo} alt="" />
                    <img className="halo-right pos-a" src={halo} alt="" />
                    <img className="halo-center pos-a" src={halo} alt="" />
                    <img
                      className="car-main pos-a center-xy"
                      src={mainCar}
                      alt=""
                      data-level={index}
                      onAnimationEnd={onAnimationEnd}
                      onAnimationStart={onAnimationStart}
                    />
                  </div>
                </div>
              </AspectRatioContainer>
            </div>
          </div>
        );
      })}
      <div className="player pos-a bottom-0 center-x p-0_75 mb-0_75 bg-cool-gray-90">
        <div className="d-flex justify-content-center mb-1">
          {playerButtons.map(({ label, iconClassName, className: buttonClassName, onClick }) => (
            <button
              className={classnames('border-0 p-0 bg-transparent size-24 mx-1_5 text-cool-gray-50', buttonClassName)}
              key={label}
              onClick={onClick}
              style={{ height: '24px', width: '24px' }}
            >
              <span className="visually-hidden">{label}</span>
              <i className={iconClassName} />
            </button>
          ))}
        </div>
        <div
          className="progress w-100"
          style={{
            height: '0.5rem',
            backgroundSize: `${(100 * level) / (LEVEL_MAP.length - 1) || 4}% 100%`,
          }}
        />
      </div>
      <ShareThisChart
        className={classnames('pos-a', { 'bottom-0 left-0 ms-1 mb-1': !isMobile, 'top-0 right-0 mt-1 me-1': isMobile })}
        shareText="Share this"
        pageTitle="Levels of self-driving"
        pageUrl={SELF_DRIVING_CARS_ARTICLE}
        shareableWidgetData={{
          widgetName: 'most-popular-cars',
          hostPageOverride: 'https://www.edmunds.com/widgets/self-driving-cars/',
          minWidth: 320,
          minHeight: 600,
          defaultTitle: 'Levels of self-driving',
          ...(isMobile ? { defaultWidth: 375, defaultHeight: 703 } : { defaultWidth: 740, defaultHeight: 630 }),
        }}
      />
    </div>
  );
}

SelfDrivingCarsUI.propTypes = {
  className: PropTypes.string,
  isMobile: PropTypes.bool,
};

SelfDrivingCarsUI.defaultProps = {
  className: null,
  isMobile: false,
};

export const mapStateToProps = state => ({
  isMobile: get(state, 'mobile'),
});

export const SelfDrivingCars = connect(mapStateToProps)(SelfDrivingCarsUI);
